import { LayoutHeader } from "@/Header";
import { Dialog } from "@/Dialog";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useBusinessNoticeTempDetail } from "~/services/business/notice";
import { useDeleteTempBusinessNotice } from "~/services/business/notify";
import SendSavedInform from "@/business/savedNotifySend/SendSavedInform";
import SendSavedSchedule from "@/business/savedNotifySend/SendSavedSchedule";
import SendSavedNotify from "@/business/savedNotifySend/SendSavedNotify";

interface TemplateParams {
  noticeSeq?: string;
  edit?: string;
}

export const Route = createFileRoute("/business/layout/notify-saved-notify")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): TemplateParams => {
    return {
      noticeSeq: search.noticeSeq as string,
      edit: search.edit as string,
    };
  },
});

function RouteComponent() {
  const { noticeSeq, edit } = Route.useSearch();
  const router = useRouter();
  // 발송된 알림장 수정 진입 여부
  const editMode = edit === "Y";

  // 임시 저장된 알림장 상세 정보 조회
  const {
    data: tempDetail,
    isLoading,
    error,
  } = useBusinessNoticeTempDetail({
    noticeSeq: Number(noticeSeq),
  });
  const [selected, setSelected] = useState<number | null>(null);

  // 저장 알림장 삭제 (수정 모드에서는 발송 완료본이므로 삭제 불가)
  const deleteMutation = useDeleteTempBusinessNotice();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{
    title: string;
    message: string;
    done: boolean;
  } | null>(null);

  const handleDelete = () => {
    const seq = Number(noticeSeq);
    if (!seq) return;
    deleteMutation.mutate(seq, {
      onSuccess: (response) => {
        setShowDeleteConfirm(false);
        if (response.statusCode === 200) {
          setDeleteResult({
            title: "알림장 삭제 완료",
            message: "저장된 알림장이 삭제되었습니다.",
            done: true,
          });
        } else {
          setDeleteResult({
            title: "알림장 삭제 실패",
            message: response.message || "알림장 삭제에 실패했습니다.",
            done: false,
          });
        }
      },
      onError: (err) => {
        console.error("저장 알림장 삭제 실패:", err);
        setShowDeleteConfirm(false);
        setDeleteResult({
          title: "알림장 삭제 실패",
          message: "알림장 삭제에 실패했습니다. 다시 시도해주세요.",
          done: false,
        });
      },
    });
  };

  const deleteButton = !editMode ? (
    <button
      type="button"
      onClick={() => setShowDeleteConfirm(true)}
      disabled={deleteMutation.isPending}
      className="px-2 py-1 text-[14px] text-[#666666] disabled:opacity-50"
    >
      삭제
    </button>
  ) : undefined;

  const deleteDialogs = (
    <>
      {/* 삭제 확인 */}
      <Dialog
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">저장 알림장 삭제</h3>
          <p className="mb-4 text-gray-600">
            저장된 알림장을 삭제할까요?
            <br />
            삭제한 알림장은 되돌릴 수 없습니다.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteMutation.isPending}
              className="flex-1 py-2 font-semibold text-gray-700 bg-white rounded-lg border border-gray-300"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-lg disabled:opacity-50"
            >
              {deleteMutation.isPending ? "삭제 중..." : "삭제"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* 삭제 결과 */}
      <Dialog
        visible={deleteResult !== null}
        onClose={() => setDeleteResult(null)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">{deleteResult?.title}</h3>
          <p className="mb-4 text-gray-600">{deleteResult?.message}</p>
          <button
            type="button"
            onClick={() => {
              const done = deleteResult?.done;
              setDeleteResult(null);
              if (done) router.navigate({ to: "/business/layout/notify" });
            }}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );

  console.log("임시 저장된 알림장 상세 정보:", tempDetail);
  console.log("로딩 상태:", isLoading);
  console.log("에러:", error);

  useEffect(() => {
    if (tempDetail?.data) {
      const selectedNoticeType = tempDetail.data.noticeTypes?.find(
        (item) => item.selected
      );
      if (selectedNoticeType?.cdNotiType === "NOTI_DIARY") {
        setSelected(0);
      } else if (selectedNoticeType?.cdNotiType === "NOTI_SCHEDULE") {
        setSelected(1);
      } else if (selectedNoticeType?.cdNotiType === "NOTI_ANNOUNCEMENT") {
        setSelected(2);
      } else {
        // 기본값으로 알림장 설정
        setSelected(0);
      }
    }
  }, [tempDetail]);

  // 데이터가 없는 경우 처리
  // 상세 조회가 실패한 저장 알림장도 목록에서 정리할 수 있도록 삭제 버튼은 그대로 둔다.
  if (!tempDetail?.data) {
    return (
      <>
        <LayoutHeader
          title={editMode ? "알림장 수정" : "알림장"}
          hasBackBtn
          hasFootPrint={false}
          rightContent={!isLoading ? deleteButton : undefined}
        />
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
          <div className="flex justify-center items-center h-full">
            <div className="text-gray-500">
              {isLoading ? "불러오는 중..." : "알림장 정보를 찾을 수 없습니다."}
            </div>
          </div>
        </div>
        {deleteDialogs}
      </>
    );
  }

  return (
    <>
      <LayoutHeader
        title={editMode ? "알림장 수정" : "알림장"}
        hasBackBtn
        hasFootPrint={false}
        rightContent={deleteButton}
      />
      {/* 수신자 */}
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
        {/* 수정 모드에서는 유형 전환 탭을 숨긴다 (발송된 알림장의 유형은 변경 불가).
            탭이 만들던 상단 여백(py-5)이 사라지므로 동일한 여백을 대신 넣어준다. */}
        {editMode && <div className="pt-5 bg-[#F5F5F5]" />}
        {!editMode && (
          <div className="flex gap-2 bg-[#F5F5F5] px-5 py-5 justify-center">
            {["알림장", "일정 공지", "공지사항"].map((label, idx) => (
              <button
                key={label}
                className={`px-5 py-1.5 rounded-full border font-medium text-[#666666] w-full
                ${
                  selected === idx
                    ? "border-orange-400 text-orange-500"
                    : "border-gray-300 text-gray-500"
                }
                bg-white`}
                style={selected === idx ? { borderWidth: 1 } : {}}
                // onClick={() => setSelected(idx)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {selected === 0 && (
          <SendSavedInform data={tempDetail.data} editMode={editMode} />
        )}
        {selected === 1 && (
          <SendSavedSchedule data={tempDetail.data} editMode={editMode} />
        )}
        {selected === 2 && (
          <SendSavedNotify data={tempDetail.data} editMode={editMode} />
        )}
      </div>
      {deleteDialogs}
    </>
  );
}
